/**
 * Environment-aware logging utilities
 * Only shows detailed logs in development mode
 */

const env = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env ?? {};
const isDev = env.DEV === true || env.DEV === 'true';

export const logger = {
  /**
   * Log general messages (dev only)
   */
  log: (...args: any[]) => {
    if (isDev) {
      console.log(...args);
    }
  },

  /**
   * Log warning messages (always shown but simplified in production)
   */
  warn: (...args: any[]) => {
    if (isDev) {
      console.warn(...args);
    } else {
      // In production, only log first argument if it's a simple message
      const firstArg = args[0];
      if (typeof firstArg === 'string' && !firstArg.includes('0x') && !firstArg.includes('addr:')) {
        console.warn(firstArg.substring(0, 100)); // Truncate long messages
      }
    }
  },

  /**
   * Log error messages (always shown but sanitized in production)
   */
  error: (...args: any[]) => {
    if (isDev) {
      console.error(...args);
    } else {
      // In production, sanitize error messages
      const firstArg = args[0];
      if (firstArg instanceof Error) {
        // Don't expose stack traces or detailed error messages
        console.error('An error occurred');
      } else if (typeof firstArg === 'string') {
        // Remove sensitive information like addresses, signatures, etc.
        const sanitized = firstArg
          .replace(/0x[a-fA-F0-9]+/g, '[REDACTED]')
          .replace(/[A-Za-z0-9]{44,}/g, '[REDACTED]')
          .replace(/addr:[A-Za-z0-9]+/g, '[REDACTED]')
          .replace(/order:[A-Za-z0-9_]+/g, '[REDACTED]');

        // Only log if it doesn't contain backend paths or internal errors
        if (!sanitized.includes('programs/') && !sanitized.includes('src/') && !sanitized.includes('at ')) {
          console.error(sanitized.substring(0, 100));
        }
      }
    }
  },

  /**
   * Log debug information (dev only)
   */
  debug: (...args: any[]) => {
    if (isDev) {
      console.debug(...args);
    }
  },

  /**
   * Log info messages (dev only)
   */
  info: (...args: any[]) => {
    if (isDev) {
      console.info(...args);
    }
  },
};

/**
 * Get a generic error message for production
 */
export function getGenericErrorMessage(error: any): string {
  if (isDev) {
    return error?.message || String(error);
  }

  // In production, return generic messages based on error type
  const errorMsg = error?.message?.toLowerCase() || '';

  if (errorMsg.includes('insufficient') || errorMsg.includes('balance')) {
    return 'Insufficient balance. Please try again.';
  }

  if (errorMsg.includes('rate') || errorMsg.includes('429') || errorMsg.includes('too many')) {
    return 'Network is busy. Please try again in a moment.';
  }

  if (errorMsg.includes('timeout') || errorMsg.includes('timed out')) {
    return 'Request timed out. Please try again.';
  }

  if (errorMsg.includes('network') || errorMsg.includes('connection')) {
    return 'Network error. Please check your connection.';
  }

  if (errorMsg.includes('wallet') || errorMsg.includes('rejected')) {
    return 'Transaction cancelled.';
  }

  // Default generic message
  return 'An error occurred. Please try again.';
}
